import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Tree, Drop, Butterfly, Recycle, CircleNotch, Sparkle, Info, Car,
} from "@phosphor-icons/react";
import LocationPicker from "@/components/LocationPicker";
import { fetchImpactLocation } from "@/lib/api";

function ScoreBar({ value, max = 100, colorGood = "#00FA9A", colorWarn = "#FFC24B", colorBad = "#FF5500" }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color = value > 60 ? colorGood : value > 30 ? colorWarn : colorBad;
  return (
    <div className="w-full h-2 bg-white/5 rounded-sm overflow-hidden">
      <div style={{ width: `${pct}%`, backgroundColor: color }} className="h-full" />
    </div>
  );
}

function ImpactTile({ icon: Icon, title, bigNumber, unit, equivalent, methodology, testid }) {
  return (
    <div className="eid-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-[#00FA9A]" weight="fill" />
        <div className="eid-label">{title}</div>
      </div>
      <div className="mt-2 text-3xl font-medium tracking-tight">
        {bigNumber}
        {unit && <span className="text-base font-normal text-zinc-400 ml-1">{unit}</span>}
      </div>
      {equivalent && (
        <div className="text-sm text-zinc-400 mt-1">{equivalent}</div>
      )}
      {methodology && (
        <details className="mt-2">
          <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-zinc-400">
            Wie berechnet?
          </summary>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{methodology}</p>
        </details>
      )}
    </div>
  );
}

export default function Impact() {
  const [picked, setPicked] = useState(null);
  const [areaHa, setAreaHa] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p, ha = areaHa) => {
    if (!p?.loc_id) return;
    setLoading(true);
    setData(null);
    try {
      const d = await fetchImpactLocation(p.loc_id, ha);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const onPick = (p) => {
    setPicked(p);
    load(p, areaHa);
  };

  const onAreaChange = (e) => {
    const ha = Math.max(0.1, Math.min(500, Number(e.target.value) || 10));
    setAreaHa(ha);
    if (picked) load(picked, ha);
  };

  return (
    <div className="space-y-6" data-testid="impact-page">
      <Helmet><title>Impact-Rechner · bodenblick.com</title></Helmet>

      <div className="eid-card p-6 md:p-8" data-testid="impact-hero">
        <div className="flex items-center gap-2"><Sparkle size={20} className="text-[#00FA9A]" weight="fill" /><div className="eid-label">Impact-Rechner</div></div>
        <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
          Was dein Feld für Klima, Tier & Mensch leistet.
        </h1>
        <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
          Wähl deine Region, gib deine Hektar an — wir rechnen dir vor, wie viel CO₂ deine
          Böden binden, wie es dem Humus geht, wie vielen Lebewesen deine Fläche Heimat
          bietet und wie viel Wasser du sparst.
        </p>
      </div>

      <div className="eid-card p-5 space-y-3">
        <LocationPicker onPick={onPick} testid="impact-picker" />
        <div className="flex items-center gap-3 pt-2 border-t border-white/10">
          <label className="eid-label">Hektar</label>
          <input
            type="number" min={0.1} max={500} step={0.5}
            value={areaHa}
            onChange={onAreaChange}
            data-testid="impact-area-input"
            className="w-24 border border-white/10 rounded-sm px-2 py-1 text-sm font-mono"
          />
          <span className="text-[11px] text-zinc-500">ha deiner Fläche</span>
        </div>
      </div>

      {loading && (
        <div className="eid-card p-6 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Rechne …</div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <ImpactTile
              icon={Tree}
              title="CO₂-Bindung"
              bigNumber={data.co2.tons_per_year.toLocaleString("de-DE")}
              unit="t / Jahr"
              equivalent={`≈ ${data.co2.equivalent_trees_year.toLocaleString("de-DE")} junge Bäume`}
              methodology={data.co2.methodology}
              testid="impact-co2"
            />
            <ImpactTile
              icon={Recycle}
              title="Humus-Aufbau"
              bigNumber={data.humus.score}
              unit={`/100 · ${data.humus.trend}`}
              equivalent={`Trend: ${data.humus.trend}`}
              methodology={data.humus.methodology}
              testid="impact-humus"
            />
            <ImpactTile
              icon={Butterfly}
              title="Biodiversität"
              bigNumber={data.biodiversity.score}
              unit={`/100 · ${data.biodiversity.label}`}
              methodology={data.biodiversity.methodology}
              testid="impact-biodiversity"
            />
            <ImpactTile
              icon={Drop}
              title="Wasser gespart"
              bigNumber={(data.water.liters_saved_per_year / 1000).toLocaleString("de-DE", { maximumFractionDigits: 0 })}
              unit="m³ / Jahr"
              equivalent={`≈ ${data.water.equivalent_showers.toLocaleString("de-DE")} Duschen`}
              methodology={data.water.methodology}
              testid="impact-water"
            />
          </div>

          {/* Narrative */}
          <div
            className="eid-card p-5"
            data-testid="impact-narrative"
            style={{ background: "linear-gradient(135deg, #141414 0%, #0E1A14 100%)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={16} className="text-[#00FA9A]" weight="fill" />
              <div className="eid-label">Deine Geschichte in Zahlen</div>
            </div>
            <div className="space-y-2 text-sm leading-relaxed">
              {(data.narrative || []).map((line, i) => <p key={i}>{line}</p>)}
            </div>
          </div>

          <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2">
            <Info size={12} className="text-[#00FA9A] shrink-0 mt-0.5" />
            <span>
              <strong>Orientierungswerte.</strong> Reale Sequestrierung schwankt je nach Boden, Bewirtschaftung
              und Witterung um ±40 %. Für offizielle Zertifikate (ÖPUL, CSRD, Gold Standard) braucht es
              Vor-Ort-Beprobung oder akkreditierte Methoden.
            </span>
          </div>
        </>
      )}

      {!picked && !loading && (
        <div className="eid-card p-6 text-center text-sm text-zinc-400" data-testid="impact-empty">
          <Car size={24} className="mx-auto text-[#00FA9A]" />
          <div className="mt-2 font-medium">Wähl eine Region — wir rechnen dir den echten Wert deiner Fläche vor.</div>
        </div>
      )}
    </div>
  );
}
